import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, RefreshCw, Search, ArrowUpDown, ArrowUp, ArrowDown, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Compact single capsule: status label + tasks done/total + progress %.
function StatusPill({ status, onHold, done = 0, total = 0, pct = 0 }) {
  const cls = onHold
    ? "bg-purple-100 text-purple-800"
    : status === "Complete"
    ? "bg-emerald-100 text-emerald-800"
    : "bg-amber-100 text-amber-800";
  const label = onHold ? "On Hold" : status === "Complete" ? "Completed" : (status || "—");
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}
      title="Status · tasks done/total · progress">
      <span>{label}</span>
      <span className="font-mono opacity-70">{done}/{total}</span>
      <span className="font-mono opacity-70">{Math.round(pct || 0)}%</span>
    </span>
  );
}

const SORT_KEYS = {
  apqp_number: (p) => p.apqp_number || "",
  project_name: (p) => (p.model_name || p.project_name || "").toLowerCase(),
  part_name: (p) => (p.part_name || "").toLowerCase(),
  part_number: (p) => (p.part_number || "").toLowerCase(),
  plant_name: (p) => (p.plant_name || "").toLowerCase(),
  category: (p) => p.category || "",
  customer_name: (p) => (p.customer_name || "").toLowerCase(),
  status: (p) => (p.on_hold ? "Hold" : (p.status || "")),
  won_date: (p) => p.won_date || "",
  created_at: (p) => p.created_at || "",
};

// Milestones with a date (Customer date preferred, else AAMPL date). Empty ones are hidden.
function milestonesWithDates(ms) {
  return (ms || [])
    .map((m) => ({ event: m.event, date: (m.customer_date || m.aampl_date || "").trim() }))
    .filter((m) => m.event && m.date);
}

// Short customer label: first two words (e.g. "Road Broad Automotive … Ltd." → "Road Broad").
function shortName(name) {
  if (!name) return "—";
  return name.trim().split(/\s+/).slice(0, 2).join(" ");
}

// Kick-off = the day after the won date (auto-derived). UTC-safe to avoid TZ drift.
function nextDay(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(String(dateStr).slice(0, 10) + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return "—";
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

// APQP projects list (Won-derived) — searchable, filterable, sortable. Rows navigate to /apqp/{id}.
export default function ApqpDashboardSection() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canManageApqp = user?.role === "admin" || hasPermission("apqp.edit");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMs, setExpandedMs] = useState(new Set()); // apqp_ids with milestones shown
  const [q, setQ] = useState("");
  const [fCustomer, setFCustomer] = useState("");
  const [fUnit, setFUnit] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/apqp").catch(() => ({ data: [] }));
      setProjects(r.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function deleteProject(pr) {
    if (!window.confirm(`Delete APQP project ${pr.apqp_number} (${pr.model_name || pr.part_name || "—"})?\n\nIt moves to Trash — the tasks/plan are kept and an admin can restore it.`)) return;
    try {
      await api.delete(`/apqp/${pr.apqp_id}`);
      toast.success(`${pr.apqp_number} deleted`);
      setProjects((prev) => prev.filter((x) => x.apqp_id !== pr.apqp_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  const customers = useMemo(() => [...new Set(projects.map((p) => p.customer_name).filter(Boolean))].sort(), [projects]);
  const units = useMemo(() => [...new Set(projects.map((p) => p.plant_name).filter(Boolean))].sort(), [projects]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = projects.filter((p) => {
      if (term) {
        const hay = `${p.apqp_number || ""} ${p.model_name || ""} ${p.project_name || ""} ${p.part_name || ""} ${p.part_number || ""} ${p.customer_name || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (fCustomer && p.customer_name !== fCustomer) return false;
      if (fUnit && p.plant_name !== fUnit) return false;
      if (fCategory && (p.category || "") !== fCategory) return false;
      const effStatus = p.on_hold ? "Hold" : p.status;
      if (fStatus && effStatus !== fStatus) return false;
      return true;
    });
    const fn = SORT_KEYS[sortKey] || SORT_KEYS.created_at;
    out = [...out].sort((a, b) => {
      const va = fn(a), vb = fn(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [projects, q, fCustomer, fUnit, fCategory, fStatus, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  }

  // B — summary counts over the current (scoped + filtered) view.
  const summary = useMemo(() => {
    const total = rows.length;
    const completed = rows.filter((p) => (p.counts?.progress_pct ?? p.progress_pct ?? 0) >= 100 || p.status === "Complete").length;
    const twoW = rows.filter((p) => p.category === "2W").length;
    const fourW = rows.filter((p) => p.category === "4W").length;
    // A project is "overdue" if even ONE of its tasks is overdue (plan date passed, not done).
    const overdue = rows.filter((p) => (p.counts?.overdue ?? 0) > 0).length;
    return { total, completed, pending: total - completed, twoW, fourW, overdue };
  }, [rows]);
  const SortIcon = ({ k }) => sortKey !== k ? <ArrowUpDown size={11} className="inline text-slate-300" /> : (sortDir === "asc" ? <ArrowUp size={11} className="inline text-orange-500" /> : <ArrowDown size={11} className="inline text-orange-500" />);
  const Th = ({ k, label, extra = "" }) => (
    <th className={`text-left px-3 py-2 cursor-pointer select-none hover:text-slate-700 ${extra}`} onClick={() => toggleSort(k)} data-testid={`apqp-sort-${k}`}>
      {label} <SortIcon k={k} />
    </th>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="apqp-dashboard-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList size={18} className="text-orange-600" />
          <h2 className="text-base font-semibold text-slate-900">APQP Projects</h2>
          <span className="text-xs text-slate-400">({rows.length}{rows.length !== projects.length ? ` / ${projects.length}` : ""})</span>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-600 p-1" title="Refresh" data-testid="apqp-refresh"><RefreshCw size={14} /></button>
      </div>

      {/* B — summary cards */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-3" data-testid="apqp-summary-cards">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="apqp-card-total">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Total</div>
          <div className="text-2xl font-bold text-slate-800">{summary.total}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="apqp-card-completed">
          <div className="text-[10px] uppercase tracking-wider text-emerald-600">Completed</div>
          <div className="text-2xl font-bold text-emerald-700">{summary.completed}</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="apqp-card-pending">
          <div className="text-[10px] uppercase tracking-wider text-amber-600">In Progress</div>
          <div className="text-2xl font-bold text-amber-700">{summary.pending}</div>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3" data-testid="apqp-card-overdue" title="Projects with at least one overdue task (plan date passed, task not complete)">
          <div className="text-[10px] uppercase tracking-wider text-red-600">Overdue</div>
          <div className="text-2xl font-bold text-red-700">{summary.overdue}</div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3" data-testid="apqp-card-2w">
          <div className="text-[10px] uppercase tracking-wider text-sky-600">2 Wheeler</div>
          <div className="text-2xl font-bold text-sky-700">{summary.twoW}</div>
        </div>
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3" data-testid="apqp-card-4w">
          <div className="text-[10px] uppercase tracking-wider text-indigo-600">4 Wheeler</div>
          <div className="text-2xl font-bold text-indigo-700">{summary.fourW}</div>
        </div>
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search APQP no, project, part no, customer…"
            className="w-full border border-slate-200 rounded pl-8 pr-3 py-1.5 text-sm" data-testid="apqp-search" />
        </div>
        <select value={fCustomer} onChange={(e) => setFCustomer(e.target.value)} className="border border-slate-200 rounded px-2 py-1.5 text-xs bg-white" data-testid="apqp-filter-customer">
          <option value="">All Customers</option>
          {customers.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={fUnit} onChange={(e) => setFUnit(e.target.value)} className="border border-slate-200 rounded px-2 py-1.5 text-xs bg-white" data-testid="apqp-filter-unit">
          <option value="">All Units</option>
          {units.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className="border border-slate-200 rounded px-2 py-1.5 text-xs bg-white" data-testid="apqp-filter-category">
          <option value="">All Categories</option>
          <option value="2W">2W</option>
          <option value="4W">4W</option>
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="border border-slate-200 rounded px-2 py-1.5 text-xs bg-white" data-testid="apqp-filter-status">
          <option value="">All Status</option>
          <option value="WIP">WIP</option>
          <option value="Hold">On Hold</option>
          <option value="Complete">Completed</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="apqp-recent-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <Th k="apqp_number" label="APQP No." />
              <Th k="project_name" label="P / Model" />
              <Th k="part_name" label="Part Name" />
              <Th k="part_number" label="Part No." />
              <Th k="plant_name" label="Unit" />
              <Th k="category" label="Category" />
              <Th k="customer_name" label="Customer" />
              <Th k="status" label="Status" extra="w-44" />
              <th className="text-left px-3 py-2 w-40">Milestone</th>
              <Th k="won_date" label="Kick-off" extra="w-28" />
              <th className="text-right px-3 py-2 w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-xs text-slate-400">No matching APQP projects. They appear automatically when a costing is marked Won.</td></tr>
            )}
            {rows.map((pr) => (
              <tr key={pr.apqp_id} className="hover:bg-slate-50 cursor-pointer" data-testid={`apqp-row-${pr.apqp_id}`}
                  onClick={() => navigate(`/apqp/${pr.apqp_id}`)}>
                <td className="px-3 py-2">
                  <button className="font-semibold text-orange-700 hover:underline" data-testid={`apqp-open-${pr.apqp_id}`}>{pr.apqp_number}</button>
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className="text-slate-800 font-medium">{pr.model_name || pr.project_name || "—"}</span>
                  {pr.is_prototype && (
                    <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 uppercase tracking-wide whitespace-nowrap" data-testid={`apqp-proto-badge-${pr.apqp_id}`}>Proto PRJ</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{pr.part_name || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{pr.part_number || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{pr.plant_name || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {pr.category ? <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-semibold">{pr.category}</span> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600" title={pr.customer_name || ""}>{shortName(pr.customer_name)}</td>
                <td className="px-3 py-2">
                  <StatusPill status={pr.status} onHold={pr.on_hold}
                    done={pr.counts?.completed ?? 0} total={pr.counts?.total ?? 0}
                    pct={pr.counts?.progress_pct ?? pr.progress_pct ?? 0} />
                </td>
                <td className="px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()}>
                  {(() => {
                    const ms = milestonesWithDates(pr.milestones);
                    if (!ms.length) return <span className="text-slate-300">—</span>;
                    const open = expandedMs.has(pr.apqp_id);
                    return (
                      <div>
                        <button
                          onClick={() => setExpandedMs((prev) => { const n = new Set(prev); n.has(pr.apqp_id) ? n.delete(pr.apqp_id) : n.add(pr.apqp_id); return n; })}
                          className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium" data-testid={`apqp-ms-toggle-${pr.apqp_id}`}>
                          {open ? "Hide milestones ▲" : `See milestones (${ms.length}) ▾`}
                        </button>
                        {open && (
                          <div className="mt-1 flex flex-col items-start gap-0.5">
                            {ms.map((m, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-1 py-px rounded bg-indigo-50 border border-indigo-100 text-[9px] leading-tight whitespace-nowrap">
                                <span className="text-indigo-700 font-semibold">{m.event}</span>
                                <span className="text-slate-500">{m.date}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500" title={pr.won_date ? `Won ${(pr.won_date).slice(0,10)} → kick-off next day` : ""}>{nextDay(pr.won_date)}</td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => navigate(`/apqp/${pr.apqp_id}`)} className="text-slate-400 hover:text-orange-600" title="Edit — open the APQP plan" data-testid={`apqp-edit-${pr.apqp_id}`}><Pencil size={14} /></button>
                    {canManageApqp && (
                      <button onClick={() => deleteProject(pr)} className="text-slate-400 hover:text-red-600" title="Delete this APQP project (moves to Trash)" data-testid={`apqp-delete-${pr.apqp_id}`}><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
